import React, { useCallback, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { downloadSampleFile } from '../services/global/global';

const FileUploadModal = ({ 
    show, 
    onHide, 
    onFileUpload, 
    onDownloadSample,
    title = "File Upload",
    acceptedFileTypes = ".xlsx,.xls,.csv",
    sampleFileName, // This will be computed dynamically
    uploadButtonText = "Upload File",
    downloadButtonText = "Download Sample",
    uploadPath // Add uploadPath for dynamic naming
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    // Generate dynamic sample file name if not provided
    const displaySampleFileName = sampleFileName || `${uploadPath}_sample.csv`;



    const downloadFileHandler = useCallback(async () => {
        try {
            console.log(sampleFileName);
            await downloadSampleFile( sampleFileName);

        } catch (error) {
            console.error("Error fetching File:", error);
        }
    }, []);
    
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        try {
            await onFileUpload(selectedFile);
            setSelectedFile(null);
            onHide();
        } catch (error) {
            // Error handling is done in the parent component
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadSample = () => {
        if (onDownloadSample) {
            downloadFileHandler();
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        onHide();
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            setSelectedFile(files[0]);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="md" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-cloud-upload me-2"></i>
                    {title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="text-center mb-4">
                    <p className="text-muted mb-3">
                        Upload your Excel file or download a sample template to get started.
                    </p>
                    
                    {/* Download Sample Button */}
                    <div className="mb-4">
                        <Button 
                            variant="outline-primary" 
                            onClick={handleDownloadSample}
                            className="me-2"
                        >
                            <i className="bi bi-download me-2"></i>
                            {downloadButtonText}
                        </Button>
                        <small className="text-muted d-block mt-1">
                            Download: <code>{displaySampleFileName}</code>
                        </small>
                    </div>

                    <div className="border-top pt-4">
                        <h6 className="mb-3">Upload Your File</h6>
                        
                        {/* Drag & Drop Area */}
                        <div 
                            className={`border border-2 border-dashed rounded p-4 mb-3 ${
                                dragOver ? 'border-primary bg-light' : 'border-secondary'
                            }`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            style={{ cursor: 'pointer' }}
                        >
                            <input
                                type="file"
                                id="fileInput"
                                accept={acceptedFileTypes}
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            
                            <label htmlFor="fileInput" style={{ cursor: 'pointer', width: '100%' }}>
                                <div className="text-center">
                                    <i className="bi bi-cloud-upload fs-1 text-muted mb-2 d-block"></i>
                                    {selectedFile ? (
                                        <div>
                                            <div className="text-success mb-2">
                                                <i className="bi bi-check-circle me-1"></i>
                                                File Selected
                                            </div>
                                            <div className="fw-bold">{selectedFile.name}</div>
                                            <div className="text-muted small">
                                                Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="fw-bold mb-1">
                                                Drag & drop your file here
                                            </div>
                                            <div className="text-muted">
                                                or click to browse
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </label>
                        </div>

                        {/* File Info */}
                        <small className="text-muted">
                            Supported formats: {acceptedFileTypes.replace(/\./g, '').toUpperCase()}
                            <br />
                            Maximum file size: 10MB
                        </small>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex justify-content-between w-100 align-items-center">
                    <small className="text-muted">
                        {selectedFile ? (
                            <span className="text-success">
                                <i className="bi bi-file-earmark-check me-1"></i>
                                Ready to upload
                            </span>
                        ) : (
                            <span>
                                <i className="bi bi-info-circle me-1"></i>
                                Please select a file to upload
                            </span>
                        )}
                    </small>
                    
                    <div className="d-flex gap-2">
                        <Button variant="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleUpload}
                            disabled={!selectedFile || isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-upload me-2"></i>
                                    {uploadButtonText}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default FileUploadModal;
